import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography, Chip, Collapse, Link } from '@mui/material';
import { getEncryptedFilesByLocalIds, EncryptedFileRecord } from '../../services/consents';
import { useDecryptedFiles } from '../../hooks/useDecryptedFiles';
import ClinicalImageTile from './ClinicalImageTile';
import UnavailableImage from './UnavailableImage';
import EnlargedImageDialog from './EnlargedImageDialog';

export interface EncryptedRef {
    type: 'encrypted_ref';
    localId: string;
    qaMetrics?: Record<string, number>;
    qaFlags?: Record<string, boolean>;
    qualityScore?: number;
    identifiableFeatures?: boolean;
}

interface EncryptedImageProps {
    uuidInvestigation: string;
    encryptedRefs: EncryptedRef[];
    label?: string;
}

export default function EncryptedImage({ uuidInvestigation, encryptedRefs, label }: EncryptedImageProps) {
    const [fileRecords, setFileRecords] = useState<EncryptedFileRecord[]>([]);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [enlargedUrl, setEnlargedUrl] = useState<string | null>(null);

    const kekBase64 = useMemo(() => {
        return localStorage.getItem(`kek_${uuidInvestigation}_images`);
    }, [uuidInvestigation]);

    // Stabilize dependency on encryptedRefs by serializing localIds
    const localIdsKey = encryptedRefs.map(r => r.localId).join(',');

    // Resolve localIds → EncryptedFileRecords (s3Key, nonce)
    useEffect(() => {
        const localIds = localIdsKey.split(',').filter(Boolean);
        if (localIds.length === 0) {
            setFetchLoading(false);
            return;
        }

        (async () => {
            try {
                const resp = await getEncryptedFilesByLocalIds(uuidInvestigation, localIds);
                if (resp.status === 200 && resp.files) {
                    setFileRecords(resp.files);
                } else {
                    setFetchError('Failed to resolve encrypted file references');
                }
            } catch (err: any) {
                setFetchError(err.message || 'Failed to fetch encrypted files');
            } finally {
                setFetchLoading(false);
            }
        })();
    }, [uuidInvestigation, localIdsKey]);

    const { decryptedFiles, loading: decryptLoading, error: decryptError } = useDecryptedFiles(
        uuidInvestigation,
        fileRecords,
        kekBase64
    );

    const loading = fetchLoading || decryptLoading;
    const error = fetchError || decryptError;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, ml: 3 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                    Decrypting images...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ ml: 3 }}>
                {label && <Typography variant="h6" color="textPrimary">{label}: </Typography>}
                <Typography variant="body2" color="error">{error}</Typography>
            </Box>
        );
    }

    if (!kekBase64) {
        return (
            <Box sx={{ ml: 3 }}>
                {label && <Typography variant="h6" color="textPrimary">{label}: </Typography>}
                <Typography variant="body2" color="text.secondary">
                    Encryption key not available
                </Typography>
            </Box>
        );
    }

    if (decryptedFiles.length === 0) {
        return (
            <Box sx={{ ml: 3 }}>
                {label && <Typography variant="h6" color="textPrimary">{label}: </Typography>}
                <Typography variant="body2">-</Typography>
            </Box>
        );
    }

    const refByLocalId = new Map(encryptedRefs.map(r => [r.localId, r]));

    return (
        <Box sx={{ ml: 3 }}>
            {label && <Typography variant="h6" color="textPrimary">{label}: </Typography>}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                {decryptedFiles.map((df) => {
                    const ref = refByLocalId.get(df.localId);
                    // SKIN-206 — an image we could not show gets an explicit failure in
                    // its own slot instead of a broken <img>. Asked once, here, at the only
                    // point that knows the answer: past it `blobUrl` is a string, so "a
                    // metadata row only ever accompanies pixels that exist" is carried by
                    // the types rather than by a runtime guard a later edit could drop.
                    //
                    // Straight to `UnavailableImage`, not through the tile. Both surfaces
                    // already shared this component before SKIN-281; routing it through the
                    // tile would build a union here only to re-discriminate it there and
                    // hand back the same props. QC's card keeps the mirror image of this.
                    if (!df.blobUrl) {
                        return (
                            <UnavailableImage key={df.localId} failedAt={df.failedAt} error={df.error} />
                        );
                    }
                    return (
                        <ImageWithMeta
                            key={df.localId}
                            blobUrl={df.blobUrl}
                            ref_={ref}
                            onEnlarge={() => setEnlargedUrl(df.blobUrl)}
                        />
                    );
                })}
            </Box>
            <EnlargedImageDialog url={enlargedUrl} onClose={() => setEnlargedUrl(null)} />
        </Box>
    );
}

interface ImageWithMetaProps {
    blobUrl: string;
    ref_?: EncryptedRef;
    onEnlarge: () => void;
}

function ImageWithMeta({ blobUrl, ref_, onEnlarge }: ImageWithMetaProps) {
    const [showMetrics, setShowMetrics] = useState(false);
    const score = ref_?.qualityScore;
    const flagsActive = ref_?.qaFlags
        ? Object.entries(ref_.qaFlags).filter(([, v]) => v).map(([k]) => k)
        : [];
    const identifiable = ref_?.identifiableFeatures;
    const metrics = ref_?.qaMetrics;
    const scoreColor = score === undefined ? 'default'
        : score >= 70 ? 'success'
        : score >= 40 ? 'warning'
        : 'error';
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: 160 }}>
            <ClinicalImageTile blobUrl={blobUrl} width={160} height={160} framed onEnlarge={onEnlarge} />
            {ref_ && (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                        {score !== undefined && (
                            <Chip
                                label={`Quality ${score}/100`}
                                size="small"
                                color={scoreColor as any}
                                variant="outlined"
                            />
                        )}
                        {identifiable !== undefined && (
                            <Chip
                                label={`Identifiable: ${identifiable ? 'Yes' : 'No'}`}
                                size="small"
                                color={identifiable ? 'warning' : 'default'}
                                variant="outlined"
                            />
                        )}
                    </Box>
                    {flagsActive.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {flagsActive.map((f) => (
                                <Chip key={f} label={`⚠ ${f}`} size="small" color="warning" />
                            ))}
                        </Box>
                    )}
                    {metrics && Object.keys(metrics).length > 0 && (
                        <>
                            <Link
                                component="button"
                                variant="caption"
                                onClick={() => setShowMetrics(s => !s)}
                                sx={{ textAlign: 'left' }}
                            >
                                {showMetrics ? 'Hide metrics' : 'Show metrics'}
                            </Link>
                            <Collapse in={showMetrics}>
                                <Box
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 11,
                                        color: 'text.secondary',
                                        bgcolor: 'grey.50',
                                        p: 0.5,
                                        borderRadius: 0.5,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {Object.entries(metrics)
                                        .map(([k, v]) => `${k}: ${typeof v === 'number' ? v.toFixed(2) : v}`)
                                        .join('\n')}
                                </Box>
                            </Collapse>
                        </>
                    )}
                </>
            )}
        </Box>
    );
}
